import { apiRequest } from './api';

/** @type {Map<string, Promise<object>>} Profiles warmed by hovering a person's name. */
const warmedProfiles = new Map();

/**
 * Get the current user's profile.
 * @returns {Promise<object>} Current profile response
 */
export async function getCurrentProfile() {
  return apiRequest('/profile/me');
}

/**
 * Get another user's public profile.
 * @param {string} uuid - The profile uuid
 * @param {object} options - Fetch options, e.g. an AbortSignal
 * @returns {Promise<object>} Public profile response
 */
export async function getPublicProfile(uuid, options = {}) {
  return apiRequest(`/profiles/${uuid}`, options);
}

/**
 * Warm another user's public profile so opening the panel does not wait on the network.
 * @param {string|null} uuid - The profile uuid
 */
export function prefetchPublicProfile(uuid) {
  if (!uuid || warmedProfiles.has(uuid)) return;

  const request = getPublicProfile(uuid);

  // A warm-up nobody asked for must not surface an error. Dropping it here leaves the
  // click-time read to fetch again and report the failure through its own handling.
  request.catch(() => warmedProfiles.delete(uuid));

  warmedProfiles.set(uuid, request);
}

/**
 * Get another user's public profile, reusing the copy hover already warmed.
 * @param {string} uuid - The profile uuid
 * @param {object} options - Fetch options, e.g. an AbortSignal
 * @returns {Promise<object>} Public profile response
 */
export function loadPublicProfile(uuid, options = {}) {
  return warmedProfiles.get(uuid) ?? getPublicProfile(uuid, options);
}

/**
 * Update the current user's editable profile fields.
 * @param {object} data - Profile fields to update
 * @returns {Promise<object>} Updated current profile response
 */
export async function updateCurrentProfile(data) {
  return apiRequest('/profile/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Update the current user's stored profile preferences.
 * @param {object} data - Preferences to update
 * @returns {Promise<object>} Updated current profile response
 */
export async function updateCurrentProfilePreferences(data) {
  return apiRequest('/profile/me/preferences', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Update the current user's profile visibility setting.
 * @param {boolean} profileVisibility - Whether the profile should be visible
 * @returns {Promise<object>} Updated current profile response
 */
export async function updateProfileVisibility(profileVisibility) {
  return apiRequest('/profile/me/visibility', {
    method: 'PATCH',
    body: JSON.stringify({ profile_visibility: profileVisibility }),
  });
}

/**
 * Update the current user's timezone settings.
 * @param {object} data - Timezone settings to update
 * @returns {Promise<object>} Updated current profile response
 */
export async function updateProfileTimezone(data) {
  return apiRequest('/profile/me/timezone', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}
